// SettingsMembersPane.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { buildBaseMemberProfile, getBaseMemberDisplayName } from 'utilities/BaseMembers';
import { PhotoShape } from 'utilities/Enums';

// CSS
import './SettingsMembersPane.css';

// Components
import SettingsMembersSeatLimitPanel from './SettingsMembersSeatLimitPanel';
import SettingsModalPaneHeader from './SettingsModalPaneHeader';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import Button from 'components/button/Button';
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';
import SelectInput from 'components/selectinput/SelectInput';

const ROLE_OWNER = 'owner';

/**
 * SettingsMembersPane
 *
 * Renders space member controls, including invites, role changes, and removals.
 */
const SettingsMembersPane = React.memo(({
  theme,
  subtitle,
  canInviteMembers,
  canUpgradePlan,
  isAtSeatLimit,
  onInviteClick,
  onUpgradePlanClick,
  seatLimitDescription,
  base,
  errorMessage,
  statusMessage,
  sortedMembers,
  user,
  canManageTarget,
  handleRoleChange,
  handleRemoveMember,
  roleOptions = [],
  getMemberRoleLabel,
}) => {

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- HANDLERS ----- //

  /**
   * Opens invite flow for the active base.
   */

  const handleInviteClick = () => {
    onInviteClick && onInviteClick(base);
  };

  /**
* Handles upgrade plan click.
*/
  const handleUpgradePlanClick = () => {
    onUpgradePlanClick && onUpgradePlanClick();
  };

  const showInviteButton = canInviteMembers && !isAtSeatLimit;

  // ----- RENDER ----- //

  return (
    <>
      <SettingsModalPaneHeader
        title="Members"
        subtitle={subtitle}
        rightContent={showInviteButton ? (
          <Button
            iconKey="person_add"
            label="Invite Members"
            enabled={showInviteButton}
            onClick={handleInviteClick}
          />
        ) : null}
      />

      <SettingsModalPaneContents>

        <SettingsMembersSeatLimitPanel
          canUpgradePlan={canUpgradePlan}
          isAtSeatLimit={isAtSeatLimit}
          seatLimitDescription={seatLimitDescription}
          theme={theme}
          onUpgradePlanClick={handleUpgradePlanClick}
        />

        {!canInviteMembers && !isAtSeatLimit && (
          <div
            className="settings-modal-note"
            style={{
              color: theme.foregroundMuted,
            }}
          >
            You can view members. Only admins and owners can invite or remove members.
          </div>
        )}

        {errorMessage && (
          <div className="settings-modal-error">{errorMessage}</div>
        )}

        {statusMessage && !errorMessage && (
          <div className="settings-modal-status">{statusMessage}</div>
        )}

        {/* MEMBERS LIST */}
        <div className="settings-modal-members-list">
          {sortedMembers.map((member) => {
            const memberProfile = buildBaseMemberProfile(member);
            const key = memberProfile.userKey || memberProfile.key;
            const roleKey = String(member?.role || ROLE_OWNER).trim() || ROLE_OWNER;
            const roleLabel = typeof getMemberRoleLabel === 'function'
              ? getMemberRoleLabel(member)
              : roleKey;
            const isSelf = key === user?.key;
            const displayName = getBaseMemberDisplayName(member);
            const selectOptions = roleKey === ROLE_OWNER
              ? [{ value: ROLE_OWNER, label: roleLabel }, ...roleOptions]
              : roleOptions.some((option) => option.value === roleKey)
                ? roleOptions
                : [{ value: roleKey, label: roleLabel }, ...roleOptions];

            return (
              <div
                className="settings-modal-member-row"
                style={{
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                }}
                key={key}
              >

                {/* MEMBER INFO */}
                <div className="settings-modal-member-info">
                  <ProfilePhoto
                    node={memberProfile}
                    size={32}
                    shape={PhotoShape.SQUARE}
                  />

                  {/* MEMBER META */}
                  <div className="settings-modal-member-meta">
                    <div
                      className="settings-modal-setting-label"
                      style={{
                        color: theme.foreground,
                      }}
                    >
                      {displayName}
                    </div>
                    <div
                      className="settings-modal-setting-description"
                      style={{
                        color: theme.foregroundMuted,
                      }}
                    >
                      {member.email || key}
                    </div>
                  </div>
                </div>

                {!isSelf && (
                  <div
                    className="settings-modal-member-actions"
                    style={{
                      paddingLeft: isMobile ? '40px' : '0px',
                    }}
                  >
                    <SelectInput
                      style={{
                        backgroundColor: "transparent",
                        borderRadius: '6px',
                        height: '40px',
                        minWidth: 100,
                        fontSize: '10pt',
                        fontWeight: '400',
                      }}
                      endIconKey="keyboard_arrow_down"
                      iconSize={16}
                      value={roleKey}
                      disabled={!canManageTarget(member, 'changeRole')}
                      options={selectOptions}
                      onChange={(event) => handleRoleChange(member, event.target.value)}
                    />

                    <Button
                      iconKey="person_remove"
                      //label="Remove"
                      backgroundColor="transparent"
                      borderColor={theme.outlineMuted}
                      borderRadius="6px"
                      enabled={canManageTarget(member, 'remove')}
                      height="40px"
                      width="40px"
                      fontSize="12px"
                      onClick={() => handleRemoveMember(member)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </SettingsModalPaneContents>
    </>
  );
});

// Dev Tools
SettingsMembersPane.displayName = 'SettingsMembersPane';

export default SettingsMembersPane;
